import type { Thread } from '@eformulieren/model'
import { useCallback, useMemo } from 'react'
import type * as Y from 'yjs'
import { useSnapshot } from '../document/snapshot.js'
import { threadsMap, threadsOf } from '../document/sectionDocument.js'

/**
 * De draadjes van dit sectiedocument, meelezend (R-73, R-72).
 *
 * Een opmerking van een medeschrijver hoort meteen in beeld te komen, net als zijn tekst.
 *
 * **`observeDeep` en niet `observe`.** Een antwoord landt in de berichtenlijst van één draadje en
 * niet in de map erboven; met een oppervlakkige observer zou een nieuw bericht pas verschijnen
 * bij de volgende hertekening om een andere reden. En de pod stempelt de auteur in een tweede
 * transactie, dus een bericht verandert nog een keer nadat het is geplaatst.
 *
 * **Langs `useSnapshot` en niet rechtstreeks langs `useSyncExternalStore`.** `threadsOf`
 * maakt per aanroep een nieuwe lijst, en dat laat React eindeloos hertekenen; de eerste versie van
 * deze hook had dat en de tests raakten het niet, want die gaven de draadjes als prop mee in
 * plaats van via de hook. De uitleg staat in `document/momentopname.ts`.
 */
export function useThreads(doc: Y.Doc | null): readonly Thread[] {
  const map = useMemo(() => (doc === null ? undefined : threadsMap(doc)), [doc])

  const subscribe = useCallback(
    (hook: () => void) => {
      if (map === undefined) return () => undefined
      map.observeDeep(hook)
      return () => map.unobserveDeep(hook)
    },
    [map],
  )

  const read = useCallback(() => (doc === null ? EMPTY : threadsOf(doc)), [doc])
  return useSnapshot(subscribe, read)
}

/** Eén lege lijst, zodat een document zonder draadjes niet elke lezing iets nieuws oplevert. */
const EMPTY: readonly Thread[] = []

/**
 * De draadjes van alle sectiedocumenten samen, per sectie (R-73).
 *
 * Eén hook over de hele verzameling en niet `useThreads` per sectie, en dat is geen
 * optimalisatie maar een regel van React: het aantal hooks per render moet vast staan, en een
 * lus over een lijst secties geeft die garantie niet. Dezelfde vorm als `useStand`, die met
 * dezelfde vraag te maken heeft.
 *
 * Een plat object en geen `Map`, want `useSnapshot` vergelijkt op inhoud en een `Map`
 * serialiseert niet.
 */
export function useThreadsPerSection(
  documents: ReadonlyMap<string, Y.Doc>,
): Readonly<Record<string, readonly Thread[]>> {
  const subscribe = useCallback(
    (hook: () => void) => {
      const docs = [...documents.values()]
      for (const doc of docs) doc.on('update', hook)
      return () => {
        for (const doc of docs) doc.off('update', hook)
      }
    },
    [documents],
  )

  const read = useCallback(() => {
    const out: Record<string, readonly Thread[]> = {}
    for (const [sectionId, doc] of documents) {
      const threads = threadsOf(doc)
      if (threads.length > 0) out[sectionId] = threads
    }
    return out
  }, [documents])

  return useSnapshot(subscribe, read)
}

/**
 * Hoeveel van deze draadjes er nog openstaan (R-73).
 *
 * Een eigen functie en geen filter op de plek van gebruik, want dit is de regel die het getal
 * betekenis geeft: een opgelost draadje telt niet mee. Wie dat ergens anders nog eens uitschrijft,
 * kan het net anders doen.
 */
export function outstandingCount(threads: readonly Thread[]): number {
  return threads.filter((thread) => !thread.opgelost).length
}

/** De draadjes die bij dit veld horen, in de orde die `threadsOf` geeft. */
export function threadsForField(threads: readonly Thread[], fieldId: string): readonly Thread[] {
  return threads.filter((thread) => thread.veldId === fieldId)
}
